// Combat HUD + in-world overlays (Phase 5 Step 15b; BossHpHUD split out
// in Step 17d). Ability bar, self HP, target ring, entity HP bars,
// damage numbers. BossHpHUD lives in combat-ui-boss-hud.ts.

import { Application, Container, Graphics, Text } from "pixi.js";
import { getAbility, type AbilityId } from "../../shared/abilities.ts";
import {
  BASIC_ATTACK_ABILITY, cooldownRemainingRatio, SLOT_LOADOUT,
  type CombatClientState, type FeedbackEntry,
} from "./combat-state.ts";
import { createPositionCache, resolveWithCache, type PositionCache } from "./position-cache.ts";
import { BossHpHUD } from "./combat-ui-boss-hud.ts";

// Layout constants — kept inline as they're pure presentation, not
// cross-entity tuning knobs. Each uses a `contracts:allow-literal`
// marker per the per-line contract check.

const SLOT_SIZE_PX = 48; // contracts:allow-literal — ability bar slot side length
const SLOT_GAP_PX = 4; // contracts:allow-literal — gap between slots
const SLOT_MARGIN_PX = 16; // contracts:allow-literal — bottom-left margin
const SLOT_BG = 0x222233;
const SLOT_BORDER = 0x555577;
const COOLDOWN_OVERLAY_COLOR = 0x000000;
const COOLDOWN_OVERLAY_ALPHA = 0.6; // contracts:allow-literal — cooldown dim alpha

const HP_HUD_MARGIN_PX = 8; // contracts:allow-literal — top-left margin
const HP_HUD_WIDTH_PX = 180; // contracts:allow-literal — self-HP bar width
const HP_HUD_HEIGHT_PX = 18; // contracts:allow-literal — self-HP bar height
const HP_BG = 0x333333;
const HP_FILL = 0x66ff66;

const ENTITY_HP_BAR_W = 36; // contracts:allow-literal — entity HP bar width
const ENTITY_HP_BAR_H = 4; // contracts:allow-literal — entity HP bar height
const ENTITY_HP_BAR_OFFSET_Y = 20; // contracts:allow-literal — above entity

const TARGET_RING_RADIUS = 20; // contracts:allow-literal — target ring radius
const TARGET_RING_COLOR = 0xffff66;
const TARGET_RING_WIDTH = 2; // contracts:allow-literal — outline thickness

const FEEDBACK_RISE_PX = 40; // contracts:allow-literal — float distance over lifetime
const FEEDBACK_FONT_PX = 14; // contracts:allow-literal — damage-number font size

// Resolves world-space coordinates for an entity id (mob | player |
// local player). Returned by the caller each frame; allows the UI to
// stay decoupled from the concrete renderer/roster classes.
export type EntityPositionResolver = (entityId: string) => { x: number; y: number } | null;

// ------------------ AbilityBar ------------------

class AbilityBar {
  private readonly container = new Container();
  private readonly slots: Array<{
    root: Container;
    cooldownOverlay: Graphics;
    abilityId: AbilityId;
  }> = [];

  constructor(private readonly app: Application) {
    this.container.zIndex = 100; // contracts:allow-literal — above world
    app.stage.sortableChildren = true;
    app.stage.addChild(this.container);

    const abilities: AbilityId[] = [...SLOT_LOADOUT, BASIC_ATTACK_ABILITY];
    abilities.forEach((abilityId, idx) => {
      const slot = this.buildSlot(abilityId, idx);
      this.container.addChild(slot.root);
      this.slots.push(slot);
    });

    this.layout();
    app.renderer.on("resize", () => this.layout());
  }

  // Update cooldown overlays — called each frame from the ticker.
  update(state: CombatClientState, now: number): void {
    for (const slot of this.slots) {
      const ratio = cooldownRemainingRatio(state, slot.abilityId, now);
      slot.cooldownOverlay.visible = ratio > 0;
      // Overlay covers ratio * slot height from the top (sweep down).
      slot.cooldownOverlay.scale.y = ratio;
    }
  }

  private buildSlot(
    abilityId: AbilityId,
    idx: number,
  ): { root: Container; cooldownOverlay: Graphics; abilityId: AbilityId } {
    const root = new Container();

    const bg = new Graphics()
      .rect(0, 0, SLOT_SIZE_PX, SLOT_SIZE_PX)
      .fill({ color: SLOT_BG })
      .stroke({ color: SLOT_BORDER, width: 1 });
    root.addChild(bg);

    const abilityDef = getAbility(abilityId);
    const label = new Text({
      text: shortName(abilityDef?.name ?? abilityId),
      style: { fontSize: 10, fill: 0xffffff, fontFamily: "monospace" }, // contracts:allow-literal — slot label font
    });
    label.x = (SLOT_SIZE_PX - label.width) / 2; // contracts:allow-literal — centering idiom
    label.y = SLOT_SIZE_PX - label.height - 2; // contracts:allow-literal — bottom padding
    root.addChild(label);

    const keyLabel = new Text({
      text: keybindLabel(abilityId, idx),
      style: { fontSize: 12, fill: 0xffcc66, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — key label font
    });
    keyLabel.x = 3; // contracts:allow-literal — top-left padding
    keyLabel.y = 2; // contracts:allow-literal — top padding
    root.addChild(keyLabel);

    const cooldownOverlay = new Graphics()
      .rect(0, 0, SLOT_SIZE_PX, SLOT_SIZE_PX)
      .fill({ color: COOLDOWN_OVERLAY_COLOR, alpha: COOLDOWN_OVERLAY_ALPHA });
    cooldownOverlay.visible = false;
    cooldownOverlay.scale.y = 0;
    root.addChild(cooldownOverlay);

    return { root, cooldownOverlay, abilityId };
  }

  private layout(): void {
    const screenH = this.app.renderer.height;
    this.container.x = SLOT_MARGIN_PX;
    this.container.y = screenH - SLOT_SIZE_PX - SLOT_MARGIN_PX;
    this.slots.forEach((slot, idx) => {
      slot.root.x = idx * (SLOT_SIZE_PX + SLOT_GAP_PX);
      slot.root.y = 0;
    });
  }
}

function shortName(name: string): string {
  // Clip to 6 chars so the slot label fits without eliding visible
  // differences between loadout abilities at 10px monospace.
  return name.length <= 6 ? name : name.slice(0, 6); // contracts:allow-literal — label truncation budget
}

function keybindLabel(abilityId: AbilityId, idx: number): string {
  if (abilityId === BASIC_ATTACK_ABILITY) return "Spc";
  // Slot idx 0..3 maps to Digit1..Digit4; the 5th slot is Space.
  return String(idx + 1); // contracts:allow-literal — 1-indexed keybind display
}

// ------------------ SelfHpHUD ------------------

class SelfHpHUD {
  private readonly container = new Container();
  private readonly bg: Graphics;
  private readonly fill: Graphics;
  private readonly text: Text;

  constructor(app: Application) {
    this.container.zIndex = 100; // contracts:allow-literal — above world
    app.stage.addChild(this.container);

    this.bg = new Graphics()
      .rect(0, 0, HP_HUD_WIDTH_PX, HP_HUD_HEIGHT_PX)
      .fill({ color: HP_BG });
    this.container.addChild(this.bg);

    this.fill = new Graphics()
      .rect(0, 0, HP_HUD_WIDTH_PX, HP_HUD_HEIGHT_PX)
      .fill({ color: HP_FILL });
    this.fill.scale.x = 1;
    this.container.addChild(this.fill);

    this.text = new Text({
      text: "0 / 0",
      style: { fontSize: 12, fill: 0xffffff, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — HUD font
    });
    this.text.x = 4; // contracts:allow-literal — left padding
    this.text.y = 2; // contracts:allow-literal — top padding
    this.container.addChild(this.text);

    this.layout();
    app.renderer.on("resize", () => this.layout());
  }

  update(state: CombatClientState): void {
    const { current, max } = state.selfHp;
    this.fill.scale.x = max > 0 ? Math.max(0, current / max) : 0;
    this.text.text = `HP ${current} / ${max}`;
  }

  private layout(): void {
    this.container.x = HP_HUD_MARGIN_PX;
    this.container.y = HP_HUD_MARGIN_PX;
  }
}

// ------------------ TargetIndicator ------------------

class TargetIndicator {
  private readonly ring = new Graphics();

  constructor(worldContainer: Container) {
    this.ring.circle(0, 0, TARGET_RING_RADIUS).stroke({
      color: TARGET_RING_COLOR,
      width: TARGET_RING_WIDTH,
    });
    this.ring.visible = false;
    worldContainer.addChild(this.ring);
  }

  update(state: CombatClientState, resolve: EntityPositionResolver): void {
    if (state.targetId === null) {
      this.ring.visible = false;
      return;
    }
    const pos = resolve(state.targetId);
    if (pos === null) {
      this.ring.visible = false;
      return;
    }
    this.ring.x = pos.x;
    this.ring.y = pos.y;
    this.ring.visible = true;
  }
}

// ------------------ EntityHpBars ------------------

class EntityHpBars {
  private readonly bars = new Map<string, { root: Container; fill: Graphics }>();
  // Step 17d — fallback cache so a combatant that just died (and was
  // therefore dropped from expeditionPositions on the same tick)
  // keeps its HP bar positioned under its last-known coords instead
  // of snapping to the zonePlayers fallback for a one-frame flicker.
  private readonly positionCache: PositionCache = createPositionCache();

  constructor(private readonly worldContainer: Container) {}

  update(state: CombatClientState, resolve: EntityPositionResolver, now: number): void {
    const seen = new Set<string>();
    for (const [entityId, hp] of state.mobHp) {
      this.upsert(entityId, hp, resolve, now);
      seen.add(entityId);
    }
    for (const [entityId, hp] of state.playerHp) {
      this.upsert(entityId, hp, resolve, now);
      seen.add(entityId);
    }
    // GC entries that dropped out of the state maps (mob defeated,
    // player left expedition).
    for (const id of this.bars.keys()) {
      if (!seen.has(id)) {
        this.remove(id);
        this.positionCache.delete(id);
      }
    }
  }

  private upsert(
    entityId: string,
    hp: { current: number; max: number },
    resolve: EntityPositionResolver,
    now: number,
  ): void {
    const fresh = resolve(entityId);
    const pos = resolveWithCache(this.positionCache, entityId, fresh, now);
    let entry = this.bars.get(entityId);
    if (!entry) {
      const root = new Container();
      const bg = new Graphics()
        .rect(0, 0, ENTITY_HP_BAR_W, ENTITY_HP_BAR_H)
        .fill({ color: HP_BG });
      const fill = new Graphics()
        .rect(0, 0, ENTITY_HP_BAR_W, ENTITY_HP_BAR_H)
        .fill({ color: HP_FILL });
      root.addChild(bg);
      root.addChild(fill);
      this.worldContainer.addChild(root);
      entry = { root, fill };
      this.bars.set(entityId, entry);
    }
    if (pos === null) {
      entry.root.visible = false;
      return;
    }
    entry.root.visible = true;
    entry.root.x = pos.x - ENTITY_HP_BAR_W / 2; // contracts:allow-literal — centering idiom
    entry.root.y = pos.y - ENTITY_HP_BAR_OFFSET_Y;
    entry.fill.scale.x = hp.max > 0 ? Math.max(0, hp.current / hp.max) : 0;
  }

  private remove(entityId: string): void {
    const entry = this.bars.get(entityId);
    if (!entry) return;
    entry.root.destroy();
    this.bars.delete(entityId);
  }
}

// ------------------ DamageNumbers ------------------

class DamageNumbers {
  private readonly active = new Map<string, { text: Text; entry: FeedbackEntry }>();

  constructor(private readonly worldContainer: Container) {}

  update(
    state: CombatClientState,
    resolve: EntityPositionResolver,
    now: number,
  ): void {
    // Spawn text nodes for new feedback entries.
    for (const entry of state.feedback) {
      if (this.active.has(entry.id)) continue;
      const text = new Text({
        text: entry.text,
        style: { fontSize: FEEDBACK_FONT_PX, fill: colorFor(entry.kind), fontFamily: "monospace", fontWeight: "bold", stroke: { color: 0x000000, width: 2 } }, // contracts:allow-literal — damage-number outline
      });
      this.worldContainer.addChild(text);
      this.active.set(entry.id, { text, entry });
    }

    // GC: every active node whose entry is gone from state (tickFeedback
    // drained it) gets destroyed.
    const live = new Set(state.feedback.map((e) => e.id));
    for (const [id, rec] of this.active) {
      if (!live.has(id)) {
        rec.text.destroy();
        this.active.delete(id);
      }
    }

    // Position + fade each active entry.
    for (const { text, entry } of this.active.values()) {
      const age = now - entry.spawnedAt;
      const ratio = Math.max(0, Math.min(1, age / 1000)); // contracts:allow-literal — 1s lifetime
      const pos = resolve(entry.anchorEntityId);
      if (pos === null) {
        text.visible = false;
        continue;
      }
      text.visible = true;
      text.x = pos.x - text.width / 2; // contracts:allow-literal — centering idiom
      text.y = pos.y - ENTITY_HP_BAR_OFFSET_Y - FEEDBACK_RISE_PX * ratio;
      text.alpha = 1 - ratio;
    }
  }
}

function colorFor(kind: FeedbackEntry["kind"]): number {
  switch (kind) {
    case "damage": return 0xff7777;
    case "miss":   return 0xcccccc;
    case "heal":   return 0x77ff77;
    case "loot":   return 0xffdd66;
  }
}

// ------------------ Orchestrator ------------------

export class CombatUI {
  private readonly abilityBar: AbilityBar;
  private readonly selfHpHud: SelfHpHUD;
  private readonly bossHpHud: BossHpHUD;
  private readonly targetIndicator: TargetIndicator;
  private readonly entityHpBars: EntityHpBars;
  private readonly damageNumbers: DamageNumbers;

  constructor(app: Application, worldContainer: Container) {
    this.abilityBar = new AbilityBar(app);
    this.selfHpHud = new SelfHpHUD(app);
    this.bossHpHud = new BossHpHUD(app);
    this.targetIndicator = new TargetIndicator(worldContainer);
    this.entityHpBars = new EntityHpBars(worldContainer);
    this.damageNumbers = new DamageNumbers(worldContainer);
  }

  // Called from main.ts's ticker each frame. Resolves positions on
  // demand so stale world coords never leak into rendering.
  tick(state: CombatClientState, resolve: EntityPositionResolver, now: number): void {
    this.abilityBar.update(state, now);
    this.selfHpHud.update(state);
    this.bossHpHud.update(state);
    this.targetIndicator.update(state, resolve);
    this.entityHpBars.update(state, resolve, now);
    this.damageNumbers.update(state, resolve, now);
  }
}
